/**
 * The three call-to-action types, SYM-5856.
 *
 * CTALabel is the CANONICAL STORED VALUE and is deliberately English. The API validates against
 * exactly these three strings and turns them into Meta's type ('Apply Now' -> APPLY_NOW), so a
 * translated string must never be saved here. Meta then renders its own button in the language of
 * whoever is scrolling, which is why the stored value being English costs nothing.
 *
 * Only what the CUSTOMER READS is translated. The distinction is the whole point: were a localised
 * label ever stored, the API would reject it, and had it slipped past, the ad would publish with a
 * different button than the one chosen.
 */
export enum CTALabel {
  APPLY_NOW = 'Apply Now',
  LEARN_MORE = 'Learn More',
  SIGN_UP = 'Sign Up',
}

export type CTAKey = keyof typeof CTALabel;

/**
 * Display labels, mirroring the backend CallToAction enum.
 *
 * Covers nl, fr and de; English falls out of CTALabel itself. Those four are 97,8% of production
 * vacancies (nl 67,7%, en 23,2%, fr 5,7%, de 1,2%). The other eight vacancy-active languages are a
 * combined 2% and fall back to English, which is what they render today.
 *
 * nl and fr are READ FROM META, by Steven on 2026-08-05, out of the call-to-action dropdown in Ads
 * Manager with its interface switched to each language. They are not translations of the English;
 * they are what Meta itself writes on the button, which is what makes the editor and the published
 * ad agree.
 *
 * An earlier version of this list was written from memory and three of six were wrong. Meta
 * publishes no table, since the label is chosen by the client app from the VIEWER's locale at
 * render time; the only source is the interface itself (&locale=nl_NL on an Ads Manager URL).
 *
 * de was read the same way and came back identical to the guess it replaced. That it happened to
 * be right is not evidence the method was sound: of the six nl/fr strings guessed the same way,
 * three were wrong.
 *
 * Still absent: pl, it, es, pt, sv, da, de_CH, fi -- 2% of vacancies, rendering English.
 */
const TRANSLATIONS: Record<string, Partial<Record<CTAKey, string>>> = {
  nl: {
    APPLY_NOW: 'Nu aanvragen',
    LEARN_MORE: 'Meer info',
    SIGN_UP: 'Registreren',
  },
  fr: {
    APPLY_NOW: 'Postuler Maintenant',
    LEARN_MORE: 'En savoir plus',
    SIGN_UP: "S'inscrire",
  },
  de: {
    APPLY_NOW: 'Jetzt bewerben',
    LEARN_MORE: 'Mehr dazu',
    SIGN_UP: 'Registrieren',
  },
};

/** What to SHOW for a canonical key, in $languageCode, falling back to English. */
export const ctaLabelFor = (key: CTAKey, languageCode?: string): string =>
  (languageCode ? TRANSLATIONS[languageCode]?.[key] : undefined) ??
  CTALabel[key];

/** The canonical key a stored value belongs to, or undefined if it is not one of the three. */
export const ctaKeyOf = (
  value: string | null | undefined
): CTAKey | undefined =>
  (Object.keys(CTALabel) as CTAKey[]).find((k) => CTALabel[k] === value);

/** What to SHOW for a stored value, e.g. the mocked platform button in a preview. */
export const ctaLabelForValue = (
  value: string | null | undefined,
  languageCode?: string
): string => ctaLabelFor(ctaKeyOf(value) ?? 'SIGN_UP', languageCode);

/**
 * Dropdown options: translated label, canonical key.
 *
 * The `key` is what a caller maps back through CTALabel before saving. It is never the translated
 * string, and that is what keeps the published button independent of the display language.
 */
export const ctaOptions = (languageCode?: string) =>
  (Object.keys(CTALabel) as CTAKey[]).map((key) => ({
    key,
    label: ctaLabelFor(key, languageCode),
  }));

/**
 * What a SETTINGS FORM should show and submit for a stored value.
 *
 * Distinct from ctaLabelForValue, and the distinction is the bug it exists to prevent. A preview
 * mocks a button the platform will always draw, so falling back to Sign Up there is reasonable. A
 * form is a claim about what is stored, so falling back LIES: a brand holding a legacy free-text
 * CTA displayed "Registreren" while actually holding 'Solliciteer vandaag', and submitting the
 * form then failed validation on a field the customer had never touched -- which blocked saving
 * the brand at all.
 *
 * An unrecognised value therefore reads as UNSET. The customer sees the placeholder, picks one of
 * the three, and the legacy value is replaced rather than silently re-submitted.
 */
export const ctaFieldStateFor = (
  value: string | null | undefined,
  languageCode?: string
): { value: string; label?: string } => {
  const key = ctaKeyOf(value);

  return key
    ? { value: CTALabel[key], label: ctaLabelFor(key, languageCode) }
    : { value: '' };
};

/** @deprecated Prefer ctaOptions(languageCode). Retained for call sites with no language to hand. */
export const ctaLabels = ctaOptions();
